/**
 * @fileoverview One-time-code entry molecule.
 * @description A row of single-digit boxes for entering a numeric verification code: typing a
 * digit advances focus to the next box, Backspace on an empty box moves focus back, and pasting a
 * full code distributes it across every box. Controlled by a single string value/onChange, like
 * every other field molecule, so the caller doesn't need to manage per-box state. Boxes are laid
 * out as an N-column grid (N set via inline style, since it's a runtime prop — a Tailwind
 * arbitrary-value class can't be built from an interpolated variable) with each box capped at a
 * max width, so they shrink evenly to fit a narrow viewport instead of the row overflowing it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react'

interface OtpInputProps {
  length: number
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  'aria-label'?: string
  'data-testid'?: string
}

/**
 * @component
 * @param {OtpInputProps} props - The code's length, current value, and change handler.
 * @param {number} props.length - How many digit boxes to render.
 * @param {string} props.value - The current code, one character per entered digit.
 * @param {(value: string) => void} props.onChange - Called with the full code whenever a box
 * changes, a digit is deleted, or a code is pasted.
 * @param {boolean} [props.disabled] - Disables every box, e.g. while a verification request is in
 * flight.
 * @param {string} [props['data-testid']] - QA locator for the wrapper; each digit box gets the same
 * id suffixed with its zero-based position (e.g. two-factor-otp-input-0).
 */
function OtpInput({
  length,
  value,
  onChange,
  disabled = false,
  'data-testid': testId,
  ...props
}: OtpInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  /**
   * @description Replaces the digit at one position and reports the resulting full code.
   * @param {number} index - The box being edited.
   * @param {string} digit - The single digit typed into that box (empty string clears it).
   */
  const setDigitAt = (index: number, digit: string) => {
    const characters = value.split('')
    characters[index] = digit
    onChange(characters.join('').slice(0, length))
  }

  /**
   * @description Handles a single box's input: keeps only its last typed digit and advances
   * focus to the next box once one is entered.
   * @param {number} index - The box being edited.
   * @param {ChangeEvent<HTMLInputElement>} event - The input change event.
   */
  const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const digit = event.target.value.replace(/\D/g, '').slice(-1)
    setDigitAt(index, digit)

    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  /**
   * @description Moves focus to the previous box when Backspace is pressed on an already-empty
   * one, so deleting reads naturally right-to-left across boxes.
   * @param {number} index - The box the key was pressed in.
   * @param {KeyboardEvent<HTMLInputElement>} event - The triggering keydown event.
   */
  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  /**
   * @description Distributes a pasted code across every box in one go.
   * @param {ClipboardEvent<HTMLInputElement>} event - The triggering paste event.
   */
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const digits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)

    if (!digits) {
      return
    }

    event.preventDefault()
    onChange(digits)
    inputRefs.current[Math.min(digits.length, length - 1)]?.focus()
  }

  return (
    <div
      className="grid gap-2"
      data-testid={testId}
      style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}
    >
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          autoComplete="one-time-code"
          value={value[index] ?? ''}
          disabled={disabled}
          data-testid={testId ? `${testId}-${index}` : undefined}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          className="aspect-square w-full max-w-12 min-w-0 rounded-lg border border-input-border bg-input-bg text-center text-[20px] font-semibold text-label focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-70"
          {...props}
        />
      ))}
    </div>
  )
}

export default OtpInput
